// Layouts/Profile/parts/EditProfileModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Pencil, Mail, Phone, MapPin, Users, X, User } from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import type { Profile } from '../ProfileView';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

interface EditProfileModalProps {
  visible: boolean;
  onHide: () => void;
  profile: Profile;
  onSaved: (updated: Partial<Profile>) => void;
}

interface FormData {
  name: string;
  gender: string;
  address: string;
  contact_number: string;
}

const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft ?? G.line}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span
      style={{
        color: C.teal,
        fontWeight: 700,
        fontSize: '0.71rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

const EditProfileModal: React.FC<EditProfileModalProps> = ({ visible, onHide, profile, onSaved }) => {
  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    gender: '',
    address: '',
    contact_number: '',
  });
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (visible && profile) {
      setFormData({
        name: profile.name || '',
        gender: profile.gender || '',
        address: profile.address || '',
        contact_number: profile.contact_number || '',
      });
    }
  }, [visible, profile]);

  const update =
    (key: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onHide();
      setIsClosing(false);
    }, 300);
  };

  const showError = (detail: string) =>
    toast.current?.show({ severity: 'error', summary: 'Validation Error', detail, life: 5000 });

  const handleSubmit = async () => {
    const name = formData.name.trim();
    if (!name) return showError('Name is required');

    if (
      formData.contact_number.trim() &&
      !/^[\d\s+\-()]{7,15}$/.test(formData.contact_number.trim())
    ) {
      return showError('Enter a valid contact number');
    }

    setLoading(true);
    try {
      // PUT /api/v1/profile
      const res = await apiClient.put('/v1/profile', {
        name,
        gender: formData.gender || undefined,
        address: formData.address.trim(),
        contact_number: formData.contact_number.trim(),
      });

      if (res.data.success) {
        onSaved(res.data.data);
        setTimeout(handleClose, 200);
      } else {
        showError(res.data.error || 'Failed to update profile');
      }
    } catch (e: any) {
      showError(e.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          ...iconBox(44),
          borderRadius: '12px',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Pencil size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: '1.01rem',
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.2,
            marginBottom: '3px',
          }}
        >
          Edit Profile
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          Update your personal details
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className={`edit-profile-dialog ${isClosing ? 'edit-profile-dialog-closing' : ''}`}
        maskClassName={`edit-profile-mask ${isClosing ? 'edit-profile-mask-closing' : ''}`}
        style={{ width: '90vw', maxWidth: '520px' }}
        modal
        dismissableMask={!loading}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .edit-profile-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: editProfileMaskIn 0.3s ease; }
          .edit-profile-mask-closing { animation: editProfileMaskOut 0.3s ease forwards !important; }
          .edit-profile-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: editProfileDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
          .edit-profile-dialog-closing.p-dialog { animation: editProfileDlgOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards !important; }
          .edit-profile-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.line}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
          .edit-profile-dialog .p-dialog-header-icon { color: ${C.sub}; }
          .edit-profile-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
          @keyframes editProfileMaskIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes editProfileMaskOut { from { opacity: 1; } to { opacity: 0; } }
          @keyframes editProfileDlgIn  { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes editProfileDlgOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92) translateY(16px); } }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<Mail size={14} />} title="Personal Details" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                <GlobalInputField
                  label="Name"
                  required
                  type="text"
                  icon={User}
                  value={formData.name}
                  onChange={update('name')}
                  placeholder="Full name"
                  disabled={loading}
                  autoComplete="off"
                />

                {/* Email intentionally not editable here — owned by auth/account flow */}
                <GlobalInputField
                  label="Email"
                  type="email"
                  icon={Mail}
                  value={profile.email || ''}
                  onChange={() => {}}
                  placeholder=""
                  disabled
                  autoComplete="off"
                />

                <GlobalInputField
                  label="Contact Number"
                  type="tel"
                  icon={Phone}
                  value={formData.contact_number}
                  onChange={update('contact_number')}
                  placeholder="Contact number (optional)"
                  disabled={loading}
                  autoComplete="off"
                />

                <div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5.5px',
                      fontSize: '0.71rem',
                      fontWeight: 600,
                      color: C.sub,
                      marginBottom: '5.5px',
                    }}
                  >
                    <Users size={13} /> Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={update('gender')}
                    disabled={loading}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '0 11px',
                      borderRadius: '8px',
                      border: `1px solid ${G.line}`,
                      background: 'white',
                      color: formData.gender ? '#111827' : C.muted,
                      fontSize: '0.78rem',
                    }}
                  >
                    <option value="">Select gender (optional)</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <GlobalTextArea
                  label="Address"
                  value={formData.address}
                  onChange={update('address')}
                  placeholder="Address (optional)"
                  rows={3}
                  disabled={loading}
                  autoResize
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
            <GlobalButton
              label="Cancel"
              variant="secondary"
              icon={X}
              onClick={handleClose}
              disabled={loading}
            />
            <GlobalButton
              label={loading ? 'SAVING...' : 'SAVE CHANGES'}
              variant="primary"
              icon={Pencil}
              onClick={handleSubmit}
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EditProfileModal;